import { memo } from 'react';
import { CardContent, CardShell } from '../common';
import { twMerge } from 'tailwind-merge';
import { NoDataMessage } from '../..';
import { CampaignTypesChart } from '../../charts/CampaignTypesChart';
import type { CampaignsByCategory, PayoutsCampaignsByCategory } from '@/types/api';
import CampaignTypesModal from '../../modals/CampaignTypesModal/CampaignTypesModal';
import { Button } from '@acast-tech/decibel';
import { ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';
import useCampaignTypesCardData from './useCampaignTypesCardData';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { usePageMetadata } from '@/hooks';

/**
 * This component is used to display the campaign types card.
 * @param className - The className for the cards shell.
 * @returns The campaign types card component.
 */
const CampaignTypesCard = ({ className }: { className?: string }) => {
  const { data, isPending, isError, error } = useCampaignTypesCardData();

  return (
    <CardShell className={twMerge('flex flex-col', className)} testId="campaign-types-card" title="Campaign types">
      <CardContent
        isPending={isPending}
        isError={isError}
        error={error}
        data={data}
        renderData={(campaignsData) => <Data data={campaignsData} />}
      />
    </CardShell>
  );
};

interface DataProps {
  data: CampaignsByCategory | PayoutsCampaignsByCategory;
}

const Data = memo(({ data }: DataProps) => {
  const { page: pageType } = usePageMetadata();
  const campaignTypeItems = Object.entries(data);

  if (!pageType) throw new Error('Page type is required');

  if (campaignTypeItems?.length === 0) {
    return <NoDataMessage dataType="campaigns" />;
  }

  //TODO: open all category types in detail view
  return (
    <div className="flex flex-col justify-center w-full gap-[18px] mt-[18px]">
      <CampaignTypesChart campaignTypes={data as CampaignsByCategory} />

      <CampaignTypesModal
        renderTrigger={(openModal, startPrefetch) => (
          <Button
            className="w-fit ml-auto"
            variant="tertiary"
            glyphEnd={ChevronRightRegularGlyph}
            onClick={() => {
              const event =
                pageType === 'earnings'
                  ? MIXPANEL_EVENT.earnings.campaignTypes.moreDetailsClicked
                  : MIXPANEL_EVENT.payouts.campaignTypes.moreDetailsClicked;

              mixpanelTrack(event, {
                widget: RevenueDashboardWidget.CampaignTypes,
                page: pageType,
              });

              openModal();
            }}
            onMouseEnter={startPrefetch}
          >
            More details
          </Button>
        )}
      />
    </div>
  );
});

export default CampaignTypesCard;
